import { useEffect, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';

const stats = [
  { icon: 'ri-user-line', value: '5亿+', label: '注册用户' },
  { icon: 'ri-video-line', value: '10万+', label: '视频内容' },
  { icon: 'ri-earth-line', value: '200+', label: '覆盖国家' },
  { icon: 'ri-star-line', value: '4.8', label: '用户评分' },
];

const milestones = [
  { year: '2021', event: '黄瓜视频在线正式上线，开启免费高清视频时代' },
  { year: '2022', event: '用户突破1亿，上线4K超清画质功能' },
  { year: '2023', event: '用户突破3亿，完成多终端全平台覆盖' },
  { year: '2024', event: '上线智能推荐系统，AI算法精准匹配内容' },
  { year: '2025', event: '用户突破5亿，成为国内领先的免费视频平台' },
];

export default function AboutPage() {
  useEffect(() => {
    document.title = '关于我们 - 黄瓜视频在线';
    window.scrollTo({ top: 0, behavior: 'instant' as const });
  }, []);

  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState('');
  const formUrl = 'https://readdy.ai/api/form/d9qtqd6e3oq4jqumtqn0';

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError('');
    const form = e.currentTarget;
    const formData = new FormData(form);
    const websiteAlt = (formData.get('website_alt') as string || '').trim();

    if (websiteAlt) {
      setFormSubmitted(true);
      form.reset();
      return;
    }

    try {
      const res = await fetch(formUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (res.ok && parsed?.code === 'OK') {
        setFormSubmitted(true);
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText;
        setFormError(serverMsg || '提交失败，请稍后再试');
      }
    } catch {
      setFormError('网络错误，请稍后再试');
    }
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'AboutPage',
        '@id': 'https://www.huangguaTV.com/about#webpage',
        url: 'https://www.huangguaTV.com/about',
        name: '关于我们 - 黄瓜视频在线',
        inLanguage: 'zh-CN',
        isPartOf: { '@id': 'https://www.huangguaTV.com/#website' },
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: '首页',
              item: 'https://www.huangguaTV.com/',
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: '关于我们',
            },
          ],
        },
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutSchema) }}
      />

      <Navbar />

      <main className="pt-20 bg-background-50">
        <div className="w-full px-4 md:px-6 py-6">
          <div className="max-w-[1000px] mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>
        </div>

        <section className="w-full px-4 md:px-6 pb-14">
          <div className="max-w-[1000px] mx-auto">
            <h1 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-4">
              关于黄瓜视频在线
            </h1>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-8 max-w-[700px]">
              黄瓜视频在线是一款完全免费的在线视频播放平台，致力于为全球华语用户提供海量高清视频内容。自2021年上线以来，我们始终坚持"免费、高清、流畅"的核心理念，让每个人都能无负担地享受高品质的视频娱乐体验。
            </p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mb-14">
              {stats.map((stat) => (
                <div
                  key={stat.label}
                  className="bg-background-100 rounded-xl p-6 text-center"
                >
                  <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600">
                    <i className={`${stat.icon} text-xl`}></i>
                  </div>
                  <div className="text-2xl md:text-3xl font-bold text-foreground-900 mb-1">
                    {stat.value}
                  </div>
                  <div className="text-xs text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-[1000px] mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-4 text-center">
              发展历程
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-10">
              从创立到领先，一步一个脚印
            </p>
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-300 md:-translate-x-px"></div>
              <div className="flex flex-col gap-8">
                {milestones.map((item, index) => (
                  <div
                    key={index}
                    className={`relative flex items-start gap-6 ${
                      index % 2 === 0
                        ? 'md:flex-row md:pr-[50%]'
                        : 'md:flex-row-reverse md:pl-[50%]'
                    }`}
                  >
                    <div className="relative z-10 w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-500 text-white text-xs font-bold mt-1">
                      {index + 1}
                    </div>
                    <div className="bg-background-50 rounded-xl p-5 flex-1">
                      <div className="text-sm font-bold text-primary-600 mb-1">{item.year}</div>
                      <p className="text-sm text-foreground-600">{item.event}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
          <div className="max-w-[1000px] mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-4 text-center">
              用户口碑
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-10">
              听听用户们怎么说
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {[
                { name: '王先生', rating: 5, comment: '画质真的没话说，4K超清看剧简直是一种享受。而且完全免费，真的太良心了！强烈推荐给身边所有朋友。' },
                { name: '李女士', rating: 5, comment: '片源很丰富，想看的剧基本都能找到。加载速度也很快，在地铁上追剧完全不卡顿，体验很棒。' },
                { name: '张同学', rating: 4, comment: '作为学生党，免费这一点就非常香了。内容更新也很快，热门剧集基本同步上线。希望以后内容越来越丰富。' },
              ].map((review, index) => (
                <div key={index} className="bg-background-100 rounded-xl p-6">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i
                        key={i}
                        className={`text-sm w-4 h-4 flex items-center justify-center ${
                          i < review.rating
                            ? 'ri-star-fill text-accent-500'
                            : 'ri-star-line text-foreground-300'
                        }`}
                      ></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    "{review.comment}"
                  </p>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-secondary-200 text-secondary-700 text-sm font-bold">
                      {review.name.charAt(0)}
                    </div>
                    <span className="text-sm font-medium text-foreground-800">{review.name}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-[700px] mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-4 text-center">
              联系我们
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-10">
              有任何问题或建议，欢迎随时与我们联系
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-10">
              <div className="bg-background-50 rounded-xl p-5 text-center">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                  <i className="ri-mail-line text-lg"></i>
                </div>
                <h4 className="text-sm font-medium text-foreground-900 mb-1">电子邮箱</h4>
                <p className="text-xs text-foreground-500">contact@huangguaTV.com</p>
              </div>
              <div className="bg-background-50 rounded-xl p-5 text-center">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600">
                  <i className="ri-phone-line text-lg"></i>
                </div>
                <h4 className="text-sm font-medium text-foreground-900 mb-1">客服电话</h4>
                <p className="text-xs text-foreground-500">400-000-0000</p>
              </div>
              <div className="bg-background-50 rounded-xl p-5 text-center">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                  <i className="ri-map-pin-line text-lg"></i>
                </div>
                <h4 className="text-sm font-medium text-foreground-900 mb-1">公司地址</h4>
                <p className="text-xs text-foreground-500">北京市朝阳区科技园区</p>
              </div>
            </div>

            {formSubmitted ? (
              <div className="bg-secondary-50 border border-secondary-200 rounded-xl p-8 text-center">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-secondary-500 text-white">
                  <i className="ri-check-line text-2xl"></i>
                </div>
                <h3 className="font-heading text-lg text-foreground-900 mb-2">提交成功！</h3>
                <p className="text-sm text-foreground-500">感谢您的留言，我们会尽快与您联系。</p>
              </div>
            ) : (
              <form
                id="contact-form"
                data-readdy-form="true"
                onSubmit={handleSubmit}
                className="bg-background-50 rounded-xl p-6 md:p-8 flex flex-col gap-5"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="contact-name" className="text-sm font-medium text-foreground-700">
                      姓名 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      placeholder="请输入您的姓名"
                      className="px-4 py-2.5 text-sm bg-background-100 border border-background-200 rounded-lg text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="contact-email" className="text-sm font-medium text-foreground-700">
                      邮箱 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      placeholder="请输入您的邮箱"
                      className="px-4 py-2.5 text-sm bg-background-100 border border-background-200 rounded-lg text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors"
                    />
                  </div>
                </div>
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="contact-subject" className="text-sm font-medium text-foreground-700">
                    主题
                  </label>
                  <input
                    id="contact-subject"
                    name="subject"
                    type="text"
                    placeholder="请输入留言主题"
                    className="px-4 py-2.5 text-sm bg-background-100 border border-background-200 rounded-lg text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="contact-message" className="text-sm font-medium text-foreground-700">
                    留言内容 <span className="text-primary-500">*</span>
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    placeholder="请输入您的留言内容（最多500字）"
                    className="px-4 py-2.5 text-sm bg-background-100 border border-background-200 rounded-lg text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors resize-none"
                  ></textarea>
                </div>
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
                {formError && (
                  <p className="text-sm text-primary-600">{formError}</p>
                )}
                <button
                  type="submit"
                  className="px-6 py-3 rounded-full text-sm font-medium bg-primary-500 text-white hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  提交留言
                </button>
              </form>
            )}
          </div>
        </section>
      </main>

      <Footer />
      <ScrollToTop />
    </>
  );
}